import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, MessageSquare, FileText, Star, Mail, LogOut, Menu, X, ExternalLink, MapPin, Package, Settings } from "lucide-react";
import { adminApi, getToken, setToken } from "../../lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  { to: "/admin/destinations", label: "Destinations", icon: MapPin },
  { to: "/admin/packages", label: "Packages", icon: Package },
  { to: "/admin/blog", label: "Blog Posts", icon: FileText },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pin, setPin] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    adminApi.post("/admin/verify").then(() => setVerified(true)).catch(() => setToken("")).finally(() => setChecking(false));
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!pin) return;
    try {
      setToken(pin);
      await adminApi.post("/admin/verify");
      setVerified(true);
      toast.success("Welcome back.");
    } catch {
      setToken("");
      toast.error("Invalid access code");
    }
  };

  const logout = () => { setToken(""); setVerified(false); navigate("/admin"); };

  if (checking) return <div className="min-h-screen flex items-center justify-center"><div className="eyebrow text-foreground/50">Loading…</div></div>;

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 pb-24 bg-secondary/20">
        <form onSubmit={onLogin} className="w-full max-w-sm bg-background border border-border p-10" data-testid="admin-login-form">
          <div className="flex items-center justify-center mb-6">
            <div className="font-serif text-3xl tracking-[0.1em]">VOYAGE LUXE</div>
          </div>
          <div className="eyebrow text-center mb-8">Concierge Desk · Admin</div>
          <input
            type="password"
            data-testid="admin-pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Access code"
            className="w-full bg-transparent border-b border-border focus:border-accent transition-colors py-3 outline-none text-sm placeholder:text-foreground/30"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-8 w-full px-8 py-4 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.3em]"
          >
            Enter
          </button>
          <p className="mt-6 text-[11px] text-foreground/50 text-center">Authorised personnel only.</p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-secondary/50 border-r border-border z-40 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-20 flex items-center justify-between px-8 border-b border-border">
          <Link to="/" className="font-serif text-xl tracking-[0.1em]">VOYAGE LUXE</Link>
          <button className="lg:hidden p-1" onClick={() => setOpen(false)} aria-label="Close menu">
            <X size={18} strokeWidth={1.25} />
          </button>
        </div>
        <nav className="p-6 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 text-sm transition-colors ${
                  isActive ? "bg-foreground text-background" : "text-foreground/70 hover:bg-background hover:text-foreground"
                }`
              }
            >
              <item.icon size={16} strokeWidth={1.5} />
              <span className="uppercase text-[11px] tracking-[0.22em]">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-6 mt-auto absolute bottom-0 left-0 right-0 border-t border-border space-y-3">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/55 hover:text-accent">
            <ExternalLink size={13} strokeWidth={1.25} /> View site
          </a>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/55 hover:text-accent">
            <LogOut size={13} strokeWidth={1.25} /> Sign out
          </button>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-0 min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-border flex items-center justify-between px-6 lg:px-10 bg-background/80 backdrop-blur sticky top-0 z-20">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-menu-toggle">
            <Menu size={20} strokeWidth={1.25} />
          </button>
          <div className="eyebrow text-foreground/55">Voyage Luxe · Atelier CMS</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55">v 2.0</div>
        </header>
        <main className="p-6 lg:p-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
